import type { VoteArtworkListParams, AwardInfo } from '@/api/voteActivity';
import {
  getVoteArtworkList,
  getAwardInfo,
  setArtworkAward,
  cancelArtworkAward,
  submitAwardResult,
} from '@/api/voteActivity';
import { useTable } from '@/hooks/useTable';
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Modal,
} from 'antd';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import { ARTWORK_COVER_PLACEHOLDER } from '@/consts/image';

const { Text } = Typography;

/** 0 未开始 1 投稿中 2 用户评审中 3 已结束 4 运营颁奖 */
const STATUS_MAP: Record<string, { text: string; color: string }> = {
  '0': { text: '未开始', color: 'default' },
  '1': { text: '投稿中', color: 'processing' },
  '2': { text: '用户评审中', color: 'warning' },
  '3': { text: '已结束', color: 'success' },
  '4': { text: '运营颁奖', color: 'purple' },
};

const VoteActivityPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  // 获取路由参数中的 id 作为 contestId
  const contestId = Number(id) || undefined;

  const [urlParams] = useSearchParams();
  const location = useLocation();
  const status = urlParams.get('status');

  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useState<VoteArtworkListParams>({
    currentPage: 1,
    pageSize: 10,
  });
  const [awardInfo, setAwardInfo] = useState<AwardInfo>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const awardInfoFetchedRef = useRef<{ contestId: number; t: number } | null>(null);

  const refreshAwardInfo = async () => {
    if (!contestId) return;
    try {
      const res = await getAwardInfo(String(contestId));
      if (res.success) {
        setAwardInfo(res.data);
      }
    } catch (error) {
      message.error('获取获奖信息失败');
    }
  };

  useEffect(() => {
    if (!contestId) return;
    const now = Date.now();
    const last = awardInfoFetchedRef.current;
    if (last?.contestId === contestId && now - last.t < 2000) return;
    awardInfoFetchedRef.current = { contestId, t: now };
    refreshAwardInfo();
  }, [contestId]);

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
    reload,
  } = useTable({
    // 投稿列表数据来自 contest/v1/artworkPageList
    queryKey: ['vote-artwork-list', contestId],
    queryFn: async (current, size, params) => {
      const response = await getVoteArtworkList({
        ...params,
        contestId,
        currentPage: current,
        pageSize: size,
      });
      return response.data;
    },
    defaultParams: { ...searchParams, contestId },
    refetchOnMount: true, // 每次进入查看投稿页都请求 contest/v1/artworkPageList
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    setSearchParams({ ...searchParams, ...values });
    updateParams({ ...searchParams, ...values });
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      currentPage: 1,
      pageSize: 10,
    });
    updateParams({
      currentPage: 1,
      pageSize: 10,
    });
  };

  const handleBatchAward = async () => {
    if (!contestId || selectedRowKeys.length === 0) return;

    try {
      const res = await setArtworkAward({
        artworkIds: selectedRowKeys,
        contestId: String(contestId),
      });
      if (res.success) {
        message.success('设置获奖成功');
        await refreshAwardInfo();
        setSelectedRowKeys([]);
        updateParams({ currentPage, pageSize, ...searchParams }, { resetPage: false });
        await reload();
      } else {
        message.error(res.message || '设置获奖失败');
      }
    } catch (error) {
      message.error('设置获奖失败');
    }
  };

  const handleBatchCancelAward = async () => {
    if (!contestId || selectedRowKeys.length === 0) return;

    try {
      const res = await cancelArtworkAward({
        artworkIds: selectedRowKeys,
        contestId: String(contestId),
      });
      if (res.success) {
        message.success('取消获奖成功');
        await refreshAwardInfo();
        setSelectedRowKeys([]);
        updateParams({ currentPage, pageSize, ...searchParams }, { resetPage: false });
        await reload();
      } else {
        message.error(res.message || '取消获奖失败');
      }
    } catch (error) {
      message.error('取消获奖失败');
    }
  };

  const handleSingleAward = async (artworkId: string, isAwarded: boolean) => {
    if (!contestId) return;
    try {
      if (isAwarded) {
        const res = await cancelArtworkAward({
          artworkIds: [artworkId],
          contestId: String(contestId),
        });
        if (res.success) {
          message.success('取消获奖成功');
          await refreshAwardInfo();
          updateParams({ currentPage, pageSize, ...searchParams }, { resetPage: false });
          await reload();
        } else {
          message.error(res.message || '取消获奖失败');
        }
      } else {
        const res = await setArtworkAward({
          artworkIds: [artworkId],
          contestId: String(contestId),
        });
        if (res.success) {
          message.success('设置获奖成功');
          await refreshAwardInfo();
          updateParams({ currentPage, pageSize, ...searchParams }, { resetPage: false });
          await reload();
        } else {
          message.error(res.message || '设置获奖失败');
        }
      }
    } catch (error) {
      message.error(isAwarded ? '取消获奖失败' : '设置获奖失败');
    }
  };

  const handleSubmitAward = async () => {
    if (!contestId) return;

    Modal.confirm({
      title: '确认提交',
      content: '提交后评比结果将无法修改，是否确认提交？',
      onOk: async () => {
        try {
          const res = await submitAwardResult(String(contestId));
          if (res.status === 200) {
            message.success('提交评比结果成功');
            await refreshAwardInfo();
          } else if (res.status === 500) {
            message.error(res.message || '提交评比结果失败');
          }
        } catch (error) {
          message.error('提交评比结果失败');
        }
      },
    });
  };

  const handleViewDetail = (artworkId: string) => {
    navigate(`/content-review/detail?content_review_ids=${artworkId}`);
  };

  const columns = [
    {
      title: '作品图片',
      dataIndex: 'artworkImageUrl',
      key: 'artworkImageUrl',
      width: 120,
      render: (url: string) => (
        <Image
          src={url || ARTWORK_COVER_PLACEHOLDER}
          fallback={ARTWORK_COVER_PLACEHOLDER}
          alt="作品图片"
          style={{ width: 80, height: 80, objectFit: 'cover' }}
        />
      ),
    },
    {
      title: '作品标题',
      dataIndex: 'artworkTitle',
      key: 'artworkTitle',
      width: 200,
    },
    {
      title: '用户信息',
      dataIndex: 'userName',
      key: 'userName',
      width: 200,
      render: (name: string, record: any) => (
        <div>
          <div>{name}</div>
          <div style={{ color: '#999' }}>ID: {record.userSuppId}</div>
        </div>
      ),
    },
    {
      title: '用户身份',
      dataIndex: 'vipName',
      key: 'vipName',
      width: 120,
      render: (vipName: string) => {
        if (!vipName) return '-';
        return vipName.replace(/Plus/g, 'Pro');
      },
    },
    {
      title: '数据统计',
      key: 'statistics',
      width: 300,
      render: (_, record: any) => (
        <div style={{ display: 'flex', gap: '16px' }}>
          <span>热度: {record.hotScore}</span>
          <span>票数: {record.voteNum}</span>
          <span>点赞: {record.likeNum}</span>
          <span>收藏: {record.collectNum}</span>
          <span>评论: {record.commentNum}</span>
        </div>
      ),
    },
    {
      title: '获奖状态',
      dataIndex: 'isAwarded',
      key: 'isAwarded',
      width: 100,
      render: (isAwarded: boolean, record: any) => (
        <div>
          <Tag color={isAwarded ? 'success' : 'default'}>
            {isAwarded ? '已获奖' : '未获奖'}
          </Tag>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record: any) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleViewDetail(record.artworkId)}
          >
            查看详情
          </Button>
          <Button
            type="link"
            onClick={() =>
              handleSingleAward(record.artworkId, record.isAwarded)
            }
          >
            {record.isAwarded ? '取消获奖' : '设为获奖'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="link"
          icon={<LeftOutlined />}
          onClick={() => {
            const state = location.state as { fromListPage?: number; fromListPageSize?: number } | null;
            navigate('/vote-activity', {
              state: {
                refreshList: true,
                returnPage: state?.fromListPage,
                returnPageSize: state?.fromListPageSize,
              },
            });
          }}
          style={{ paddingLeft: 0 }}
        >
          返回投票活动列表
        </Button>
      </div>
      <Card
        title="投票活动"
        extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {status != null && (
            <Text type="secondary">
              {STATUS_MAP[status]?.text ?? '未知状态'}
            </Text>
          )}
          {awardInfo && (
            <>
              <Text type="secondary">
                当前获奖人数 {awardInfo.awardedNum}/{awardInfo.total}
              </Text>
              {(status === '4') && ( // 运营颁奖状态才显示提交按钮
                <Button type="primary" onClick={handleSubmitAward}>
                  提交评比结果
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="artworkTitle" label="作品标题">
            <Input placeholder="请输入作品标题" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="userName" label="用户名">
            <Input placeholder="请输入用户名" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="userSuppId" label="用户ID">
            <Input placeholder="请输入用户ID" style={{ width: 200 }} />
          </Form.Item>
          {/* <Form.Item name="vipName" label="用户身份">
            <Input placeholder="请输入用户身份" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="platform" label="充值订单">
            <Select
              style={{ width: 120 }}
              placeholder="苹果/安卓"
              allowClear
              options={[
                { label: '苹果', value: 'apple' },
                { label: '安卓', value: 'android' },
              ]}
            />
          </Form.Item> */}
          <Form.Item name="isAwarded" label="获奖状态">
            <Select
              style={{ width: 120 }}
              placeholder="请选择状态"
              allowClear
              options={[
                { label: '已获奖', value: true },
                { label: '未获奖', value: false },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={handleSearch}
              style={{ marginRight: 8 }}
            >
              搜索
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Form.Item>
        </Form>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          onClick={handleBatchAward}
          disabled={selectedRowKeys.length === 0}
          style={{ marginRight: 8 }}
        >
          批量设置获奖
        </Button>
        <Button
          onClick={handleBatchCancelAward}
          disabled={selectedRowKeys.length === 0}
        >
          批量取消获奖
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="artworkId"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        loading={isLoading}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as string[]),
        }}
      />
    </Card>
    </>
  );
};

export default VoteActivityPage;
